// client/src/components/pages/LexiStar/Replay/usePlayback.ts
//
// The replay's client-side clock (FR-RPL-001): play / pause at 1×–64× over the
// track's own time, seek, and stop at the end. The clock runs on
// requestAnimationFrame but commits to React state at most ~15 times a second
// — enough for a smooth marker, cheap for the charts and timeline.
//
// ST-00245: while the timeline is being dragged (scrubStart … scrubEnd) the
// clock holds still, so the truck stays under the pointer even when playing at
// 64×; playback resumes from wherever the scrub let go. Converted from
// usePlayback.js in the same change (CLAUDE.md TypeScript rule).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { advance } from './replayLogic';

const COMMIT_EVERY_MS = 66;

export interface PlaybackBounds {
  start: number;
  end: number;
}

export interface Playback {
  /** The replay clock (epoch ms), null without bounds. */
  cursor: number | null;
  playing: boolean;
  speed: number;
  setSpeed: (speed: number) => void;
  seek: (t: number) => void;
  toggle: () => void;
  pause: () => void;
  /** True between scrubStart and scrubEnd. */
  scrubbing: boolean;
  scrubStart: () => void;
  scrubEnd: () => void;
}

export default function usePlayback(
  bounds: PlaybackBounds | null | undefined,
  { initialSpeed = 8 }: { initialSpeed?: number } = {}
): Playback {
  const start = bounds?.start ?? null;
  const end = bounds?.end ?? null;
  const [cursor, setCursor] = useState<number | null>(start);
  const [playing, setPlaying] = useState(false);
  const [scrubbing, setScrubbing] = useState(false);
  const [speed, setSpeed] = useState(initialSpeed);
  const cursorRef = useRef<number | null>(start);

  // A new window resets the clock to its start. A layout effect, so the reset lands in the same
  // commit as the new bounds: a seek made right after the window appears is never undone by a
  // reset that ran a frame later.
  useLayoutEffect(() => {
    cursorRef.current = start;
    setCursor(start);
    setPlaying(false);
    setScrubbing(false);
  }, [start, end]);

  const running = playing && !scrubbing;
  useEffect(() => {
    if (!running || start === null || end === null) return undefined;
    const raf: (fn: (now: number) => void) => number =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (fn) => window.setTimeout(() => fn(Date.now()), 16);
    const caf: (handle: number) => void =
      typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame.bind(window)
        : window.clearTimeout.bind(window);
    let handle = 0;
    let last: number | null = null;
    let lastCommit = 0;
    const tick = (now: number) => {
      if (last === null) last = now;
      cursorRef.current = advance(cursorRef.current ?? start, now - last, speed, end);
      last = now;
      const done = cursorRef.current >= end;
      if (done || now - lastCommit >= COMMIT_EVERY_MS) {
        lastCommit = now;
        setCursor(cursorRef.current);
      }
      if (done) {
        setPlaying(false);
        return;
      }
      handle = raf(tick);
    };
    handle = raf(tick);
    return () => caf(handle);
  }, [running, speed, start, end]);

  const seek = useCallback(
    (t: number) => {
      if (start === null || end === null || !Number.isFinite(t)) return;
      const c = Math.max(start, Math.min(end, t));
      cursorRef.current = c;
      setCursor(c);
    },
    [start, end]
  );

  const toggle = useCallback(() => {
    if (start === null || end === null) return;
    setPlaying((p) => {
      if (!p && (cursorRef.current ?? start) >= end) {
        cursorRef.current = start;
        setCursor(start);
      }
      return !p;
    });
  }, [start, end]);

  const pause = useCallback(() => setPlaying(false), []);
  const scrubStart = useCallback(() => setScrubbing(true), []);
  const scrubEnd = useCallback(() => setScrubbing(false), []);

  return {
    // Until the reset effect has run for new bounds the state is still null:
    // the clock is at the start then, never "nowhere" (a seek in that frame
    // must not be dropped).
    cursor: cursor ?? start,
    playing,
    speed,
    setSpeed,
    seek,
    toggle,
    pause,
    scrubbing,
    scrubStart,
    scrubEnd,
  };
}
